<template>
  <div>
    <!-- Title: Rendered only if there are bookings with Reservation status -->
    <PageSection
      :title="t('reservations.nextStay')"
      :display-title="hasReservationStatus"
    >
      <!-- Placeholder when no current bookings / quotes -->
      <BookingPlaceholder
        v-if="!bookings?.length"
        :title-key="emptyTitleKey"
        :description-key="emptyDescriptionKey"
      >
        <template #icon>
          <SlopesIcon />
        </template>
      </BookingPlaceholder>

      <!-- Current Bookings -->
      <template v-else>
        <div v-for="(booking, index) in sortedBookings" :key="booking.reference">
          <!-- Booking Component -->
          <Booking :booking="booking" />

          <!-- Separator or Margin -->
          <div v-if="index < sortedBookings.length - 1" class="separator my-4"></div>
          <div v-else class="mb-5"></div>
        </div>

        <EurostarTicketsBox
          v-if="eurostarDisplay"
          :reference="eurostarDisplay.reference"
          class="mt-2 mb-4"
        />
      </template>
    </PageSection>
  </div>
</template>

<script setup lang="ts">
  import { defineProps, computed } from 'vue'
  import { useI18n } from 'vue-i18n'

  // UI Components
  import BookingPlaceholder from '@/components/ConfirmedReservations/BookingPlaceholder.vue'
  import Booking from '@/components/common/Booking.vue'
  import EurostarTicketsBox from '@/components/common/EurostarTicketsBox.vue'
  import PageSection from '@/components/UI/PageSection.vue'

  // Icons
  import SlopesIcon from '@/assets/SlopesIcon.vue'

  // Types
  import { BookingSummary } from '@/services/BookingService.interface'
  import { BookingStatus } from '@/config'
  import { useBookingStatus } from '@/composables/useBookingStatus'
  import { useStore } from '@/store'
  import { getEurostarDisplayForBookings } from '@/utils/eurostarTickets'

  // Props
  const props = defineProps({
    bookings: {
      type: Array as () => BookingSummary[],
      required: true,
      default: () => [],
    },
    emptyTitleKey: {
      type: String,
      default: 'reservations.noBooking',
    },
    emptyDescriptionKey: {
      type: String,
      default: 'reservations.timeToChoose',
    },
  })

  // Translation
  const { t } = useI18n()
  const store = useStore()

  // Computed: Check if there are bookings with "Reservation" status
  const hasReservationStatus = computed(() =>
    props.bookings.some(booking => booking.status === BookingStatus.Reservation)
  )

  // Computed: Sort bookings by appropriate date based on status
  const sortedBookings = computed(() => {
    return [...props.bookings].sort((a, b) => {
      const aStatus = useBookingStatus(a)
      const bStatus = useBookingStatus(b)

      // For reservations, use createDate
      if (aStatus.isReservationStatus.value && bStatus.isReservationStatus.value) {
        const dateA = new Date(a.createDate ?? 0).getTime()
        const dateB = new Date(b.createDate ?? 0).getTime()
        return dateB - dateA
      }
      // For quotes and cart in progress, use createVersionDate
      if (aStatus.isQuoteOrCartInProgress.value && bStatus.isQuoteOrCartInProgress.value) {
        const dateA = new Date(a.createVersionDate).getTime()
        const dateB = new Date(b.createVersionDate).getTime()
        return dateB - dateA
      }
      // Keep original order for mixed statuses
      return 0
    })
  })

  const eurostarDisplay = computed(() =>
    getEurostarDisplayForBookings(
      sortedBookings.value,
      store.getters.getLocale
    )
  )
</script>

<style scoped>
  .separator {
    border: 0;
    border-top: 1px solid #dadada; /* or any color you want */
    width: 100%;
  }
</style>
